import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Home, ShoppingCart, Clock, User, CreditCard } from 'lucide-react';
import { CartProvider } from '../../context/CartContext';
import { SignalRProvider } from '../../context/SignalRContext';
import LogoutButton from '../../components/LogoutButton';
import { useIsDesktop } from '../../utils/session';
import { motion } from "framer-motion";

const CustomerMenu = lazy(() => import('../../components/CustomerMenu'));
const OrderStatus = lazy(() => import('../../components/OrderStatus'));
const CartView = lazy(() => import('../../components/CartView'));
const CustomerPayment = lazy(() => import('../../components/CustomerPayment'));

type Tab = 'menu' | 'cart' | 'order' | 'payment' | 'history' | 'profile';

const LOCAL_STORAGE_TAB_KEY = 'activeCustomerTab';

const CustomerPanel: React.FC = () => {
  const { restaurantGuid, tableId } = useParams();
  const location = useLocation();
  const isDesktop = useIsDesktop() || window.innerWidth >= 768; // Fallback to window width if useIsDesktop is not available

  const [activeTab, setActiveTab] = useState<Tab>(() => {
    return (localStorage.getItem(LOCAL_STORAGE_TAB_KEY) as Tab) || 'menu';
  });

  // Sync tab with URL changes (e.g., when navigating back)
  useEffect(() => {
    const savedTab = localStorage.getItem(LOCAL_STORAGE_TAB_KEY) as Tab | null;
    if (
      savedTab &&
      ['menu', 'cart', 'order', 'payment', 'history', 'profile'].includes(savedTab) &&
      savedTab !== activeTab
    ) {
      setActiveTab(savedTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Save tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_TAB_KEY, activeTab);
  }, [activeTab]);

  if (!restaurantGuid || !tableId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded shadow-md text-center">
          <h2 className="text-2xl font-bold mb-2 text-red-600">Invalid QR or Link</h2>
          <p className="text-gray-600 mb-4">Please scan the QR code on your table to start ordering.</p>
        </div>
      </div>
    );
  }

  const hubUrl = `${process.env.REACT_APP_API_BASE || 'https://localhost:44381'}/customerHub?restaurantGuid=${restaurantGuid}&tableId=${tableId}`;
  const groupName = `customer-${restaurantGuid}-table-${tableId}`;

  const restaurantName = JSON.parse(localStorage.getItem('Dinelyt_session') || '{}')?.platformInfo?.restaurantName || 'Default Restaurant';
  const restaurantLogoUrl = JSON.parse(localStorage.getItem('Dinelyt_session') || '{}')?.platformInfo?.restaurantLogoUrl || '';
  const isQRMenuOrderEnabled = JSON.parse(localStorage.getItem('Dinelyt_session') || '{}')?.platformInfo?.isQRMenuOrderEnabled || false;
  const renderContent = () => {
    switch (activeTab) {
      case 'menu':
        return <CustomerMenu restaurantGuid={restaurantGuid} tableId={tableId} isQRMenuOrderEnabled={isQRMenuOrderEnabled} />;
      case 'cart':
        return <CartView restaurantGuid={restaurantGuid} tableId={tableId} />;
      case 'order':
        return <OrderStatus restaurantGuid={restaurantGuid!} tableId={tableId} />;
      case 'payment':
        return <CustomerPayment restaurantGuid={restaurantGuid!} tableId={tableId} />;
      case 'history':
        return <ComingSoon label="Order History" />;
      case 'profile':
        return <ComingSoon label="Profile" />;
      default:
        return null;
    }
  };

  return (
    <SignalRProvider hubUrl={hubUrl} groupName={groupName} userRole='customer' restaurantGuid={restaurantGuid} tableId={tableId}>
      <CartProvider>
        <div className="flex flex-col min-h-screen relative">
          {/* 🔝 Premium Apple-like Header */}
          {!isDesktop && (<motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/70 dark:bg-gray-900/60 border-b border-white/20 dark:border-gray-700/40 shadow-sm"
          >
            <div className="flex justify-between items-center w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 md:h-16">

              {/* Left: Restaurant Logo + Name */}
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">

                {/* Logo: Always visible, fixed size, and doesn't shrink */}
                {restaurantLogoUrl && (
                  <motion.img
                    src={restaurantLogoUrl}
                    alt={`${restaurantName} Logo`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}

                    // h-8 for small/default, h-10 for md and up. Logo is always preserved.
                    className="w-auto h-8 md:h-10 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 object-cover flex-shrink-0"
                  />
                )}

                {/* Name: Conditional Display and Sizing */}
                <h1 className={`
                      font-bold text-gray-800 dark:text-white tracking-tight 
                      
                      // Default (xs) screens: Name is visible with smallest font
                      text-sm sm:text-base 
                      
                      // Only hide on extra, extra small devices (if needed) - let's remove 'hidden' for now
                      
                      // Medium Screens: Full font size
                      md:text-xl
                      
                      // Add 'truncate' to prevent overlap on small screens
                      truncate max-w-[150px]
                  `}>
                  {restaurantName}
                </h1>
              </div>

              {/* Right: Logout Button (never shrinks) */}
              <div className="flex items-center flex-shrink-0">
                <LogoutButton />
              </div>
            </div>
          </motion.header>)}

          {/* Spacer to push content below the fixed header */}
          <div className="pt-14 md:pt-16" />

          {/* Main Content */}
          {!isDesktop && (
            <main className="flex-grow pb-24 md:pb-8">
              <Suspense fallback={<div className="text-center p-8">Loading...</div>}>
                {renderContent()}
              </Suspense>
            </main>)}

          {/* 📱 Bottom Navigation for Mobile */}
          {!isDesktop && (
            <motion.nav
              initial={{ y: 80 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-t border-gray-200/80 dark:border-gray-700/60 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
            >
              <div className="flex justify-around items-center h-16">
                <NavButton icon={<Home />} label="Menu" tab="menu" activeTab={activeTab} onClick={setActiveTab} />
                {isQRMenuOrderEnabled && (<NavButton icon={<ShoppingCart />} label="Cart" tab="cart" activeTab={activeTab} onClick={setActiveTab} />)}
                <NavButton icon={<Clock />} label="Order" tab="order" activeTab={activeTab} onClick={setActiveTab} />
                <NavButton icon={<CreditCard />} label="Payment" tab="payment" activeTab={activeTab} onClick={setActiveTab} />
                <NavButton icon={<User />} label="Profile" tab="profile" activeTab={activeTab} onClick={setActiveTab} />
              </div>
            </motion.nav>
          )}

          {/* Desktop Sidebar */}
          {isDesktop && (
            <motion.aside
              initial={{ x: -250 }}
              animate={{ x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="fixed top-0 left-0 h-full w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 p-4 flex flex-col"
            >
              <div className="flex items-center space-x-3 mb-8 pt-3">
                {restaurantLogoUrl && (
                  <img
                    src={restaurantLogoUrl}
                    alt={`${restaurantName} Logo`}
                    className="w-12 h-12 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 object-cover"
                  />
                )}
                <h1 className="text-xl font-bold text-gray-800 dark:text-white tracking-tight">
                  {restaurantName}
                </h1>
              </div>

              <nav className="flex flex-col space-y-2">
                <SidebarButton icon={<Home />} label="Menu" tab="menu" activeTab={activeTab} onClick={setActiveTab} />
                {isQRMenuOrderEnabled && (<SidebarButton icon={<ShoppingCart />} label="Cart" tab="cart" activeTab={activeTab} onClick={setActiveTab} />)}
                <SidebarButton icon={<Clock />} label="Order Status" tab="order" activeTab={activeTab} onClick={setActiveTab} />
                <SidebarButton icon={<CreditCard />} label="Payment" tab="payment" activeTab={activeTab} onClick={setActiveTab} />
                <SidebarButton icon={<User />} label="Profile" tab="profile" activeTab={activeTab} onClick={setActiveTab} />
              </nav>

              <div className="mt-auto">
                <LogoutButton />
              </div>
            </motion.aside>
          )}

          {/* Adjust main content for desktop sidebar */}
          {isDesktop && (
            <div className="pl-64">
              <main className="flex-grow p-8">
                <Suspense fallback={<div className="text-center">Loading...</div>}>
                  {renderContent()}
                </Suspense>
              </main>
            </div>
          )}
        </div>
      </CartProvider>
    </SignalRProvider>
  );
};

export default CustomerPanel;

const ComingSoon = ({ label }: { label: string }) => (
  <div className="flex flex-col items-center justify-center h-full text-gray-600">
    <h2 className="text-xl font-semibold mb-2">{label}</h2>
    <p className="text-sm">This feature is coming soon!</p>
  </div>
);

const NavButton = ({
  icon,
  label,
  tab,
  activeTab,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  tab: Tab;
  activeTab: Tab;
  onClick: (tab: Tab) => void;
}) => (
  <button
    onClick={() => onClick(tab)}
    className={`flex flex-col items-center justify-center text-xs transition-all duration-300 ease-in-out
    ${activeTab === tab ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-300'}`}
    aria-label={label}
  >
    {icon}
    <span className="mt-1">{label}</span>
  </button>
);

const SidebarButton = ({
  icon,
  label,
  tab,
  activeTab,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  tab: Tab;
  activeTab: Tab;
  onClick: (tab: Tab) => void;
}) => (
  <button
    onClick={() => onClick(tab)}
    className={`flex items-center gap-3 p-2 rounded-md text-sm font-medium transition-all duration-300 ease-in-out
    ${activeTab === tab ? 'bg-blue-500 text-white dark:bg-blue-600 dark:text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
    aria-label={label}
  >
    {icon}
    {label}
  </button>
);
